import React from 'react';
import moment from 'moment';
import Snap from 'snapsvg-cjs';

function is_object_item(item) {
  return (item && typeof item === 'object' && !Array.isArray(item) && item !== null);
}

function deep_object_assign(target, source) {
  if (is_object_item(target) && is_object_item(source)) {
    Object.keys(source).forEach((key) => {
      if (is_object_item(source[key])) {
        if (!target[key]) Object.assign(target, { [key]: {} });
        deep_object_assign(target[key], source[key]);
      } else {
        Object.assign(target, { [key]: source[key] });
      }
    });
  }
  return target;
}

function Bar(gt, task) {
  const self = {};

  function init() {
    set_defaults();
    prepare();
    draw();
    bind();
  }

  function set_defaults() {
    self.action_completed = false;
    self.task = task;
  }

  function prepare() {
    prepare_values();
    prepare_plugins();
  }

  function prepare_values() {
    const _defaults = {
      height: 20,
      show_label: gt.config.show_label || gt.config.overlap_label,
      draggable: true,
      expandable: true,
      class: '',
    };
    Object.assign(self, _defaults, gt.config.bar);
    self.invalid = self.task.invalid;
    self.column_width = gt.get_column_width();
    self.x = compute_x();
    self.y = compute_y();
    self.duration = Math.floor((self.task._end.diff(self.task._start, 'hours') + 24) / 24);
    self.width = self.column_width * self.duration;
    self.progress_width = self.width * (self.task.progress / 100) || 0;
    self.group = gt.canvas.group().addClass('bar-wrapper').addClass(self.task.custom_class || '');
    self.bar_group = gt.canvas.group().addClass('bar-group').appendTo(self.group);
    self.handle_group = gt.canvas.group().addClass('handle-group').appendTo(self.group);
    self.bar_details = gt.canvas.group().addClass('bar-details').appendTo(self.group);
    self.is_dragged = false;
    const gtConfig = gt.config;
    if (typeof gtConfig.current_user_id !== 'undefined') {
      const permission = (self.task.owner == gtConfig.current_user_id || gtConfig.is_manager || gtConfig.is_co_manager || gtConfig.is_team_admin);
      self.draggable = permission;
      self.expandable = permission;
    }
  }

  function prepare_plugins() {
    Snap.plugin((Snap, Element, Paper, global, Fragment) => {
      Element.prototype.getX = function () {
        return +this.attr('x');
      };
      Element.prototype.getY = function () {
        return +this.attr('y');
      };
      Element.prototype.getWidth = function () {
        return +this.attr('width');
      };
      Element.prototype.getHeight = function () {
        return +this.attr('height');
      };
      Element.prototype.getEndX = function () {
        return this.getX() + this.getWidth();
      };
    });
  }

  function draw() {
    draw_bar();
    draw_progress_bar();
    if (self.show_label) draw_label();
    if (self.expandable) draw_resize_handles();
  }

  function draw_bar() {
    let _class = `${self.class} ${self.task.class !== 'undefind' ? self.task.class : ''}`;
    let _style = gt.config.shadecolor ? `fill: ${self.task.color}` : '';
    if (typeof gt.config.wsId !== 'undefined' && (gt.config.wsId != self.task.wsId)) {
      _style = '';
      _class = `${_class} bar-grey`;
    }
    // console.log(`x--${self.x} y--${self.y} width--${self.width} height--${self.height} radius--${self.corner_radius}`);
    self.$bar = gt.canvas.rect(self.x, self.y,
      self.width, self.height,
      self.corner_radius, self.corner_radius)
      .addClass(`bar ${_class}`)
      .attr('style', _style)
      .appendTo(self.bar_group);
    if (self.invalid) {
      self.$bar.addClass('bar-invalid');
    }
  }

  function draw_progress_bar() {
    if (self.invalid) return;
    let _class = `${self.class} ${self.task.class !== 'undefind' ? self.task.class : ''}`;
    let _style = gt.config.shadecolor ? `fill: ${self.task.color}` : '';
    if (typeof gt.config.wsId !== 'undefined' && (gt.config.wsId != self.task.wsId)) {
      _style = '';
      _class = `${_class} bar-grey`;
    }
    self.$bar_progress = gt.canvas.rect(self.x, self.y,
      self.progress_width, self.height,
      self.corner_radius, self.corner_radius)
      .addClass(`bar-progress-style ${_class}`)
      .attr('style', _style)
      .appendTo(self.bar_group);
  }

  function draw_label() {
    let labelText;
    if (self.invalid) return;
    if (self.task.days < 0) return;
    if (gt.config.overlap_label) {
      // console.log(self.task.overlapping_items);
      labelText = self.task.overlapping_items.length || '';
    } else {
      labelText = `${self.task.days + 1}d`;
    }
    gt.canvas.text(self.width,
      self.y + self.height / 2,
      labelText)
      .addClass('bar-label')
      .appendTo(self.bar_group);
    update_label_position();
  }

  function draw_resize_handles() {
    if (self.invalid) return;
    const bar = self.$bar;
    const handle_width = 8;
    gt.canvas.rect(bar.getX() + bar.getWidth() - 9, bar.getY() + 1,
      handle_width, self.height - 2, self.corner_radius, self.corner_radius)
      .addClass(`handle right ${self.task.class}`)
      .appendTo(self.handle_group);
    gt.canvas.rect(bar.getX() + 1, bar.getY() + 1,
      handle_width, self.height - 2, self.corner_radius, self.corner_radius)
      .addClass(`handle left ${self.task.class}`)
      .appendTo(self.handle_group);
    if (self.task.progress && self.task.progress < 100) {
      gt.canvas.polygon(get_progress_polygon_points())
        .addClass('handle progress')
        .appendTo(self.handle_group);
    }
  }

  function get_progress_polygon_points() {
    const bar_progress = self.$bar_progress;
    return [
      bar_progress.getEndX() - 5, bar_progress.getY() + bar_progress.getHeight(),
      bar_progress.getEndX() + 5, bar_progress.getY() + bar_progress.getHeight(),
      bar_progress.getEndX(), bar_progress.getY() + bar_progress.getHeight() - 8.66,
    ];
  }

  function bind() {
    if (self.invalid) return;
    setup_click_event();
    if (self.expandable) bind_resize();
    if (self.draggable) bind_drag();
    if (self.expandable) bind_resize_progress();
  }

  function get_details_position() {
    return {
      x: self.$bar.getEndX() + 2,
      y: self.$bar.getY() - 10,
    };
  }

  function bind_resize() {
    const {
      left,
      right,
    } = get_handles();
    left.drag(onmove_left, onstart, onstop_left);
    right.drag(onmove_right, onstart, onstop_right);

    function onmove_right(dx, dy) {
      onmove_handle_right(dx, dy);
    }

    function onstop_right() {
      onstop_handle_right();
    }

    function onmove_left(dx, dy) {
      onmove_handle_left(dx, dy);
    }

    function onstop_left() {
      onstop_handle_left();
    }
  }

  function get_handles() {
    return {
      left: self.handle_group.select('.handle.left'),
      right: self.handle_group.select('.handle.right'),
    };
  }

  function bind_drag() {
    self.bar_group.drag(onmove, onstart, onstop);
  }

  function bind_resize_progress() {
    const bar = self.$bar;
    const bar_progress = self.$bar_progress;
    const handle = self.group.select('.handle.progress');
    handle && handle.drag(on_move, on_start, on_stop);

    function on_move(dx, dy) {
      if (dx > bar_progress.max_dx) {
        dx = bar_progress.max_dx;
      }
      if (dx < bar_progress.min_dx) {
        dx = bar_progress.min_dx;
      }

      bar_progress.attr('width', bar_progress.owidth + dx);
      handle.attr('points', get_progress_polygon_points());
      bar_progress.finaldx = dx;
    }

    function on_stop() {
      // if (!bar_progress.finaldx) return;
      progress_changed();
      set_action_completed();
    }

    function on_start() {
      bar_progress.finaldx = 0;
      bar_progress.owidth = bar_progress.getWidth();
      bar_progress.min_dx = -bar_progress.getWidth();
      bar_progress.max_dx = bar.getWidth() - bar_progress.getWidth();
    }
  }

  function onstart() {
    const bar = self.$bar;
    bar.ox = bar.getX();
    bar.oy = bar.getY();
    bar.owidth = bar.getWidth();
    bar.finaldx = 0;
    // run_method_for_dependencies('onstart');
  }
  self.onstart = onstart;

  function onmove(dx, dy) {
    const bar = self.$bar;
    bar.finaldx = get_snap_position(dx);
    const xx = bar.ox + bar.finaldx;
    const xx1 = bar.ox + bar.finaldx + bar.owidth;
    if (xx < gt.gantt_start_x_pos || xx1 > gt.gantt_end_x_pos) {
      return;
    }
    update_bar_position({
      x: xx,
    });
    // run_method_for_dependencies('onmove', [dx, dy]);
  }
  self.onmove = onmove;

  function onstop() {
    const bar = self.$bar;
    if (!bar.finaldx) return;
    date_changed();
    set_action_completed();
    // run_method_for_dependencies('onstop');
  }
  self.onstop = onstop;

  function onmove_handle_left(dx, dy) {
    const bar = self.$bar;
    bar.finaldx = get_snap_position(dx);
    const xx = bar.ox + bar.finaldx;
    if (xx < gt.gantt_start_x_pos) {
      return;
    }
    update_bar_position({
      x: bar.ox + bar.finaldx,
      width: bar.owidth - bar.finaldx,
    });
    // run_method_for_dependencies('onmove', [dx, dy]);
  }
  self.onmove_handle_left = onmove_handle_left;

  function onstop_handle_left() {
    const bar = self.$bar;
    if (bar.finaldx) date_changed();
    set_action_completed();
    // run_method_for_dependencies('onstop');
  }
  self.onstop_handle_left = onstop_handle_left;

  function run_method_for_dependencies(fn, args) {
    const dm = gt.dependency_map;
    if (dm[self.task.id]) {
      for (const deptask of dm[self.task.id]) {
        const dt = gt.get_bar(deptask);
        dt[fn].apply(dt, args);
      }
    }
  }

  function onmove_handle_right(dx, dy) {
    const bar = self.$bar;
    bar.finaldx = get_snap_position(dx);
    const xx1 = bar.ox + bar.finaldx + bar.owidth;
    if (xx1 > gt.gantt_end_x_pos) {
      return;
    }
    update_bar_position({
      width: bar.owidth + bar.finaldx,
    });
  }

  function onstop_handle_right() {
    const bar = self.$bar;
    if (bar.finaldx) date_changed();
    set_action_completed();
  }

  function update_bar_position({
    x = null,
    width = null,
  }) {
    const bar = self.$bar;
    if (x) {
      // get all x values of parent task
      //            const xs = task.dependencies.map(dep => {
      //                return gt.get_bar(dep).$bar.getX();
      //            });
      //            // child task must not go before parent
      //            const valid_x = xs.reduce((prev, curr) => {
      //                return x >= curr;
      //            }, x);
      //            if (!valid_x) {
      //                width = null;
      //                return;
      //            }
      update_attr(bar, 'x', x);
    }
    if (width && width >= self.column_width) {
      update_attr(bar, 'width', width);
    }
    update_label_position();
    update_handle_position();
    update_progressbar_position();
  }

  function setup_click_event() {
    self.group.click((e) => {
      gt.unselect_all();
    });
    self.bar_group.click((e) => {
      if (self.is_dragged) {
        self.is_dragged = false;
        return false;
      }
      setTimeout(() => gt.trigger_event('show_details', [self.task, self, e, get_details_position()]), 0);
    });
  }

  function date_changed() {
    const {
      new_start_date,
      new_end_date,
    } = compute_start_end_date();
    self.task._start = new_start_date;
    self.task._end = new_end_date;
    self.task.days = self.task._end.diff(self.task._start, 'days');
    self.is_dragged = true;
    update_label_position();
    gt.trigger_event('date_change', [self.task, new_start_date, new_end_date]);
  }

  function progress_changed() {
    const new_progress = compute_progress();
    self.task.progress = new_progress;
    self.is_dragged = true;
    gt.trigger_event('progress_change', [self.task, new_progress]);
  }

  function set_action_completed() {
    self.action_completed = true;
    setTimeout(() => self.action_completed = false, 2000);
  }

  function compute_start_end_date() {
    const bar = self.$bar;
    let barX = bar.getX();
    if (gt.config.show_task_list) {
      barX -= gt.config.task_list_width;
    }
    const colwidth = self.column_width;
    const x_in_units = barX / colwidth;
    const new_start_date = gt.gantt_start.clone().add(x_in_units * 24, 'hours');
    const width_in_units = bar.getWidth() / colwidth;
    const new_end_date = new_start_date.clone().add(width_in_units * 24, 'hours').subtract(1 * 24, 'hours');
    return {
      new_start_date,
      new_end_date,
    };
  }

  function compute_progress() {
    const progress = self.$bar_progress.getWidth() / self.$bar.getWidth() * 100;
    return parseInt(progress, 10);
  }

  function compute_x() {
    // let x = self.task._start.diff(gt.gantt_start, 'hours') /
    //     gt.config.step * gt.config.column_width;
    const colwidth = self.column_width;
    let x = self.task._start.diff(gt.gantt_start, 'days') * colwidth;
    if (gt.config.show_task_list) {
      x += gt.config.task_list_width;
    }
    return x;
  }

  function compute_y() {
    const task_index = typeof self.task._task_index !== 'undefined' ? self.task._task_index : self.task._index;
    const y = gt.header_height + (gt.config.padding / 2)
            + task_index * (self.height + gt.config.padding);
    return y;
  }

  function get_snap_position(dx) {
    const odx = dx;
    let rem; let
      position;
    const colwidth = gt.get_column_width();
    const viewmode = gt.get_view_mode();
    rem = dx % colwidth;
    if (viewmode == 'week') {
      position = odx - rem
            + ((rem < gt.config.column_width / 14) ? 0 : colwidth);
    } else if (viewmode == 'month') {
      position = odx - rem
				+ ((rem < gt.config.column_width / 60) ? 0 : colwidth);
    } else {
      position = odx - rem
            + ((rem < colwidth / 2) ? 0 : colwidth);
    }
    return position;
  }

  function update_attr(element, attr, value) {
    value = +value;
    if (!isNaN(value)) {
      element.attr(attr, value);
    }
    return element;
  }

  function update_progressbar_position() {
    self.$bar_progress.attr('x', self.$bar.getX());
    self.$bar_progress.attr('width', self.$bar.getWidth() * (self.task.progress / 100));
  }

  function update_label_position() {
    if (!self.show_label) return;
    const bar = self.$bar;
    const label = self.group.select('.bar-label');
    let labelText;
    if (gt.config.overlap_label) {
      labelText = self.task.overlapping_items.length || '';
      // console.log(bar.getWidth());
      if (bar.getWidth() > 8) {
        label.attr('x', bar.getX() + 4).attr('text', labelText);
      }
    } else if ((label.getBBox().width) > (bar.getWidth()) || (bar.getWidth()) < 30) {
      labelText = '';
      label.addClass('big').attr('x', bar.getX() + bar.getWidth() + 5).attr('text', labelText);
    } else {
      labelText = `${self.task.days + 1}d`;
      label.removeClass('big').attr('x', bar.getX() + 20).attr('text', labelText);
    }
  }

  function update_handle_position() {
    const bar = self.$bar;
    self.handle_group.select('.handle.left').attr({
      x: bar.getX() + 1,
    });
    self.handle_group.select('.handle.right').attr({
      x: bar.getEndX() - 9,
    });
    const handle = self.group.select('.handle.progress');
    handle && handle.attr('points', get_progress_polygon_points());
  }


  function isFunction(functionToCheck) {
    const getType = {};
    return functionToCheck && getType.toString.call(functionToCheck) === '[object Function]';
  }

  init();
  return self;
}

class GanttChart {
  constructor(element, options) {
    const _defaults = {
      svg_class: '',
      header_height: 50,
      show_task_list: true,
      task_list_width: 150,
      column_width: 300,
      step: 24,
      bar: {
        height: 20,
        corner_radius: 3,
      },
      padding: 18,
      view_mode: 'Day',
      date_is_integer: true,
      date_format: 'YYYY-MM-DD',
      header_month_format: 'MMM YY',
      tasks: [],
      gantt_start: null,
      gantt_end: null,
      shadecolor: false,
      overlapping_colors: ['#0FAFC2'],
      overlapping_days: 0,
      show_label: false,
      focus_today: false,
      offset: 0,
      timeline_type: '',
      sorting: {
        enabled: false,
        id: 'asc',
        items: [{ id: 'asc', title: 'A to Z' }, { id: 'desc', title: 'Z to A' }],
      },
      legend: {
        enabled: false,
        items: [],
      },
      maximum_year: false, // if set true, will show the data at maximum 1 year from start date.
      hover_date_show: false, // show date in hover of name
      overlap_label: false,
      renderpage: '',
    };
    this.element = element;
    const optionsConfig = typeof options.config !== 'undefined' ? options.config : {};
    this.config = deep_object_assign(_defaults, optionsConfig);
    this._events = Object.assign({}, options.events);
    this.element_groups = {};
    this._bars = [];
    this._tasks = options.tasks || [];
    this.trigger_event = this.trigger_event;
    this.gantt_start = this.config.gantt_start;
    this.gantt_end = this.config.gantt_end;
    this.change_view_mode(this.config.view_mode);
  }

  print_time() {
    // var date = new Date();
    // var seconds = date.getTime();
    // console.log("time-->"+seconds);
  }

  change_view_mode(mode) {
    this.print_time();
    console.log(`mode-->${mode}`);
    this.gantt_start = this.config.gantt_start || null;
    this.gantt_end = this.config.gantt_end || null;
    this.set_scale(mode);
    this.prepare();
    this.render();
  }

  change_sorting_view(sort) {
    console.log(`change_sorting_view-->${sort}`);
    this.config.sorting.id = sort;
    this.change_view_mode(this.config.view_mode);
  }

  apply_sorting_filters(tasks = []) {
    if (typeof this.config.sorting !== 'undefined') {
      const sortingId = this.config.sorting.id || '';
      if (typeof this.config.sorting.filters === 'function') {
        console.log('filters');
        return this.config.sorting.filters(tasks, sortingId);
      }
      switch (sortingId) {
        case 'asc':
          tasks = tasks.sort((a, b) => a.name.localeCompare(b.name));
          break;
        case 'desc':
          tasks = tasks.sort((a, b) => b.name.localeCompare(a.name));
          break;
        default:
          break;
      }
    }
    return tasks;
  }

  set_scale(scale) {
    this.config.view_mode = scale;
    if (scale === 'Day') {
      this.config.step = 24;
      this.config.column_width = 25;
    } else if (scale === 'Week') {
      this.config.step = 24 * 7;
      this.config.column_width = 20 * 7;
    } else if (scale === 'Month') {
      this.config.step = 24 * 301;
      this.config.column_width = 5 * 302;
    }
  }

  prepare() {
    this.prepare_tasks();
    this.prepare_dates();
    this.prepare_canvas();
  }

  prepare_tasks() {
    let tasks = this._tasks;
    if (this.config.sorting.enabled === true) {
      tasks = this.apply_sorting_filters(tasks);
    }
    tasks = tasks.filter((task, i) => {
      if (typeof task.data === 'undefined') return false;
      task.data = this.build_task(task.data, i);
      task._start = null;
      task._end = null;
      if (task.data.length) {
        for (const t of task.data) {
          if (typeof this.config.wsId !== 'undefined' && t.wsId != this.config.wsId) {
            continue;
          }
          if (!task._start || t._start < task._start) {
            task._start = t._start;
          }
          if (!task._end || t._end > task._end) {
            task._end = t._end;
          }
        }
      }
      return task;
    });
    this.tasks = tasks;
  }

  get_user_offset() {
    const { offset } = this.config;
    if (typeof offset === 'undefined' || offset == '' || offset == null) {
      return 0;
    }
    return offset;
  }

  get_timeline_type() {
    return this.config.timeline_type;
  }

  get_utc_offset_value() {
    return moment().utcOffset() * 60 * 1000;
  }

  build_task(tasks, index) {
    const timeline_type = this.get_timeline_type();
    const utc_offset_value = this.get_utc_offset_value();
    if (Array.isArray(tasks)) {
      return tasks.map((task, i) => {
        // momentify
        if (this.config.date_is_integer) {
          if (task.start) {
            if (timeline_type == 'board') {
              const tstart = (parseInt(task.start) * 1000 - utc_offset_value);
              task._start = moment(tstart);
            } else {
              task._start = moment(moment(task.start * 1000), this.config.date_format);
            }
          }
          if (task.end) {
            if (timeline_type == 'board') {
              const tend = (parseInt(task.end) * 1000 - utc_offset_value);
              task._end = moment(tend);
            } else {
              task._end = moment(moment(task.end * 1000), this.config.date_format);
            }
          }
        } else {
          task._start = moment(task.start, this.config.date_format);
          task._end = moment(task.end, this.config.date_format);
        }
        task._task_index = index;
        // cache index
        task._index = i;
        // invalid dates
        if (!task.start && !task.end) {
          task._start = moment();
          task._end = moment().startOf('day').add(1, 'year');
        }
        if (!task.start && task.end) {
          task._start = moment().utc();
          /* adding 1 day temp fix */
          task._end = moment.utc(moment(task.end * 1000)).clone().add(1, 'days');
        }
        if (task.start && !task.end) {
          task._end = moment.utc(task._start).clone().add(1, 'year');
        }

        // invalid flag
        if (!task.start || !task.end) {
          task.invalid = true;
          task.days = -1;
        } else {
          task.days = task._end.diff(task._start, 'days');
        }
        if (moment(task._start).isDST()) {
          var offset = moment(task._start).utcOffset();
          var offset = Math.abs(offset * 60 * 1000);
          task._start = moment(moment(task._start + offset), this.config.date_format);
        }
        if (moment(task._end).isDST()) {
          var offset = moment(task._end).utcOffset();
          var offset = Math.abs(offset * 60 * 1000);
          task._end = moment(moment(task._end + offset), this.config.date_format);
        }
        return task;
      });
    }
    console.log('empty tasks...');
    return [];
  }

  set_gantt_start_date(date) {
    try {
      const start_date = date;
      const hours = start_date.hours();
      const minutes = start_date.minutes();
      const seconds = start_date.seconds();
      let s = 0;
      if (hours > 0) {
        s += (hours * 3600);
      }
      if (minutes > 0) {
        s += (minutes * 60);
      }
      if (seconds > 0) {
        s += seconds;
      }
      if (s > 0) {
        s *= 1000;
      }
      this.gantt_start = moment(start_date - s);
    } catch (err_start_date) {
      console.log(err_start_date);
    }
  }

  set_gantt_end_date(date) {
    try {
      const end_date = date;
      const hours = end_date.hours();
      const minutes = end_date.minutes();
      const seconds = end_date.seconds();
      let s = 0;
      if (hours > 0) {
        s += (hours * 3600);
      }
      if (minutes > 0) {
        s += (minutes * 60);
      }
      if (seconds > 0) {
        s += seconds;
      }
      if (s > 0) {
        s *= 1000;
      }
      this.gantt_end = moment((end_date - s) + 86399000); // 23:59:59 -> 86399000 milliseconds
    } catch (err_end_date) {
      console.log(err_end_date);
    }
  }

  prepare_dates() {
    const self = this;
    self.gantt_start = (self.gantt_start > 0) ? self.gantt_start : null;
    self.gantt_end = (self.gantt_end > 0) ? self.gantt_end : null;
    console.log(`start->${self.gantt_start}`);
    console.log(`end->${self.gantt_end}`);
    if (self.config.date_is_integer) {
      if (self.gantt_start) {
        const slen = `${self.gantt_start}`;
        if (slen.length == 13) {
          self.gantt_start /= 1000;
        }
        self.gantt_start = moment(moment.utc(self.gantt_start * 1000), this.config.date_format);
      }
      if (self.gantt_end) {
        const elen = `${self.gantt_end}`;
        if (elen.length == 13) {
          self.gantt_end /= 1000;
        }
        self.gantt_end = moment(moment.utc(self.gantt_end * 1000), this.config.date_format);
      }
    } else {
      if (self.gantt_start) {
        self.gantt_start = moment(self.gantt_start, this.config.date_format);
      }
      if (self.gantt_end) {
        self.gantt_end = moment(self.gantt_end, this.config.date_format);
      }
    }

    self.set_global_dates();
    // console.log('start date-->' + self.gantt_start + ' end date-->' + self.gantt_end);
    const daysLeft = moment(self.gantt_end).diff(self.gantt_start, 'days');
    if (daysLeft < 60) {
      self.gantt_end = self.gantt_start.clone().add(60, 'days');
      self.set_gantt_end_date(self.gantt_end);
    }
    // filter tasks if task between gantt_start and gantt_end
    if (typeof this.config.wsId !== 'undefined') {
      // if days more than 1 year, then add 1 year from start_date.
      if (this.config.maximum_year == true && daysLeft > 365) {
        self.gantt_end = self.gantt_start.clone().add(1, 'year');
        self.set_gantt_end_date(self.gantt_end);
      }
      // console.log('start date-->' + self.gantt_start + ' end date-->' + self.gantt_end);
      let startDate = self.gantt_start;
      let endDate = self.gantt_end;
      const { overlapping_days } = self.config;
      let is_overlapping = false;
      if (overlapping_days && Number.isInteger(overlapping_days)) {
        is_overlapping = true;
        startDate = moment(moment.utc(startDate).clone().add(-overlapping_days, 'day'), self.config.date_format);
        endDate = moment(moment.utc(endDate).clone().add(overlapping_days, 'day'), self.config.date_format);
      }
      self.tasks = self.tasks.filter((item) => {
        if (item.data) {
          item.data = item.data.filter((item1) => {
            // check whether the task between gantt start and end.
            let valid_task = (item1._start >= self.gantt_start && item1._start <= self.gantt_end)
                        && (item1._end >= self.gantt_start && item1._end <= self.gantt_end);
            // console.log(`valid_task--> ${self.gantt_start} ${self.gantt_end}  `);
            // console.log(`valid_task1--> ${valid_task} ${item1._start}  ${item1._end}`);
            if (!valid_task && is_overlapping) {
              // check whether the overlapping task falling on between gantt start and end.
              const is_falling_task = (item1._start >= self.gantt_start && item1._start <= self.gantt_end)
                        || (item1._end >= self.gantt_start && item1._end <= self.gantt_end);
              // check whether overlapping task between new overlapping days.
              const is_valid_overlapping_task = (item1._start >= startDate && item1._start <= endDate)
                            && (item1._end >= startDate && item1._end <= endDate);

              valid_task = is_falling_task && is_valid_overlapping_task;
            }
            if (valid_task) {
              if (!item._start || item1._start < item._start) {
                item._start = item1._start;
              }
              if (!item._end || item1._end > item._end) {
                item._end = item1._end;
              }
            }
            return valid_task;
          });
        }
        return item;
      });
      // recalculate global dates.
      self.set_global_dates();
    }
    // console.log('start date2-->' + self.gantt_start + ' end date2-->' + self.gantt_end);
    this.set_gantt_dates();
    this.setup_dates();
  }

  set_global_dates() {
    for (const task of this.tasks) {
      // set global start and end date
      if (!this.gantt_start || (task._start && task._start < this.gantt_start)) {
        this.gantt_start = task._start;
      }
      if (!this.gantt_end || task._end > this.gantt_end) {
        this.gantt_end = task._end;
      }
    }
    const daysLeft = moment(this.gantt_end).diff(this.gantt_start, 'days');
    if (daysLeft < 60) {
      this.gantt_end = this.gantt_start.clone().add(60, 'days');
      this.set_gantt_end_date(this.gantt_end);
    } else if (this.config.maximum_year == true && daysLeft > 365) {
      this.gantt_end = this.gantt_start.clone().add(1, 'year');
      this.set_gantt_end_date(this.gantt_end);
    }
    // console.log(this.gantt_start);
    if (moment(this.gantt_start).isDST()) {
      var offset = moment(this.gantt_start).utcOffset();
      // console.log('offset' + offset);
      var offset = Math.abs(offset * 60 * 1000);
      this.gantt_start = moment(this.gantt_start + offset);
      // console.log(this.gantt_start);
    }
    if (moment(this.gantt_end).isDST()) {
      var offset = moment(this.gantt_end).utcOffset();
      var offset = Math.abs(offset * 60 * 1000);
      this.gantt_end = moment(this.gantt_end + offset);
    }
    this.set_gantt_start_date(this.gantt_start);
    this.set_gantt_end_date(this.gantt_end);
  }

  set_gantt_dates() {
    const self = this;
    if (self.view_is('Month')) {
      self.gantt_start = self.gantt_start.clone().startOf('month');
      self.gantt_end = self.gantt_end.clone().endOf('month');
      const diff_days = self.gantt_start.diff(self.gantt_end, 'days');
      if (diff_days < 217) {
        self.gantt_end = self.gantt_start.clone().add(1, 'year');
      }
    } else {
      self.gantt_start = self.gantt_start;
      self.gantt_end = self.gantt_end;
    }
  }

  setup_dates() {
    this.dates = [];
    let cur_date = null;
    while (cur_date === null || cur_date < this.gantt_end) {
      if (!cur_date) {
        cur_date = this.gantt_start.clone();
      } else {
        // cur_date = this.view_is('Month') ?
        //     cur_date.clone().add(1, 'month') :
        //     cur_date.clone().add(this.config.step, 'hours');
        cur_date = cur_date.clone().add(24, 'hours');
      }
      this.dates.push(cur_date);
    }
    // if cur_date is bigger than gantt_end, then remove it from dates.
    if (cur_date > this.gantt_end) {
      this.dates.pop();
    }
  }

  prepare_canvas() {
    if (this.canvas) return;
    this.canvas = Snap(this.element).addClass(`gantt ${this.config.svg_class}`);
    this.render_task_list = false;
    if (this.config.render_task_list) {
      this.render_task_list = document.querySelector(`#${this.config.render_task_list}`);
    }
  }

  render() {
    this.clear();
    this.setup_groups();
    this.make_grid();
    this.make_dates();
    this.make_bars();
    if (this.tasks.length > 0) {
      this.set_width();
      this.set_scroll_position();
    }
    this.bind_grid_click();
  }

  clear() {
    this.canvas.clear();
    this._bars = [];
  }

  setup_groups() {
    const groups = ['grid', 'date_headers', 'date', 'progress', 'bar', 'details'];
    // make group layers
    for (const group of groups) {
      this.element_groups[group] = this.canvas.group().attr({
        id: group,
      });
    }
  }

  make_grid() {
    this.make_grid_background();
    this.make_grid_header();
    this.make_grid_rows();
    this.make_grid_ticks();
    // console.log(this.tasks.length);
    if (this.tasks.length > 0) {
      this.set_width();
      this.set_scroll_position();
    }
    this.bind_grid_click();
  }

  make_grid_background() {
    const self = this;
    const colwidth = this.get_column_width();
    let grid_width = self.dates.length * colwidth;
    const grid_height = self.config.header_height
            + (self.config.bar.height + self.config.padding) * self.tasks.length;
    if (self.config.show_task_list) {
      grid_width += self.config.task_list_width;
    }
    self.canvas.rect(0, 0, grid_width, grid_height)
      .addClass('grid-background')
      .appendTo(self.element_groups.grid);
    self.canvas.attr({
      height: grid_height + 10,
      width: '100%',
    });
    if (this.render_task_list) {
      this.render_task_list.style.height = `${grid_height + 10}px`;
    }
    self.trigger_event('after_grid_render', [self.config.view_mode]);
  }

  make_grid_rows() {
    const self = this;
    const colwidth = this.get_column_width();
    const rows = self.canvas.group().appendTo(self.element_groups.grid);
    const lines = self.canvas.group().appendTo(self.element_groups.grid);
    let row_width = self.dates.length * colwidth;
    const row_height = self.config.bar.height + self.config.padding;
    let row_y = self.header_height;
    if (self.config.show_task_list) {
      row_width += self.config.task_list_width;
    }
    let task_count = 0;
        for (let task of self.tasks) { // eslint-disable-line
      self.canvas.rect(0, row_y, row_width, row_height)
        .addClass('grid-row')
        .appendTo(rows);
      self.canvas.line(0, row_y + row_height, row_width, row_y + row_height)
        .addClass('row-line')
        .appendTo(lines);
      row_y += self.config.bar.height + self.config.padding;
      if (self.render_task_list) {
        const _height = (task_count == 0) ? row_height - 1 : row_height - 2;
        const padding_top = 10;
        const row_count = task_count + 1;
        const y = self.config.header_height + row_count * (self.config.bar.height + self.config.padding);
        let truncateChar = task.name;
        let showDate = '';
        if (this.config.hover_date_show == true) {
          const startDate = (task.data[0].start) ? moment.unix(task.data[0].start).format('MMM DD, YYYY') : '';
          const endDate = (task.data[0].ws_target_date) ? moment.unix(task.data[0].ws_target_date).format('MMM DD, YYYY') : '';
          if ((startDate) && (endDate)) {
            showDate = `${startDate}&nbsp &nbsp - &nbsp &nbsp${endDate}`;
          } else if ((!startDate) && (endDate)) {
            showDate = `No Date &nbsp &nbsp - &nbsp &nbsp${endDate}`;
          } else if (((startDate) && (!endDate))) {
            showDate = `${startDate}&nbsp &nbsp - &nbsp &nbsp No Date`;
          } else {
            showDate = 'No Date &nbsp &nbsp - &nbsp &nbsp No Date';
          }
        }
        let _class = '';
        let tooltip_title = '';
        if (truncateChar.length > 20) {
          truncateChar = `${truncateChar.substr(0, 20)}..`;
          _class = 'truncateText';
          tooltip_title = task.name;
        }
        const task_count_div = document.querySelector(`#${this.config.render_task_list} .task-${task_count}`);
        if (task_count_div != null) {
          task_count_div.remove();
        }
        const weeks = task.weeks || null;
        let temp;
        if (weeks) {
          temp = $(`<div class="task-list-item task-${task_count}" style="height:${_height}px;">
                    <div class="clickable" style="padding-left: 10px; padding-top:${padding_top}px;display: inline-block;    width: 147px;" data-user-id="${task.id}" title="${task.name}" id="task_name_${task_count}">${truncateChar}</div>
                    <div class="clickable" style="text-align: right;display: inline-block;width: 37px;    color: #2C9AB7;  font-size: 11px" data-user-name="${task.name}" data-user-id="${task.id}" id="task_week_${task_count}">${weeks}</div>
                </div>`);
        } else {
          temp = $(`<div class="task-list-item task-${task_count}" style="height:${_height}px;">
                            <div class="clickable" style="padding-left: 10px; padding-top:${padding_top}px;" id="task_name_${task_count}">${truncateChar}</div>
                        </div>`);
          if (this.config.hover_date_show == true) {
            temp = $(`<div class="task-list-item task-${task_count}" style="height:${_height}px;">
                            <div class="clickable" style="padding-left: 10px; padding-top:${padding_top}px;" id="task_name_${task_count}" title="${tooltip_title}" >${truncateChar}</div>
                            <div ws_id = ${task.id} class="task-list-item-date task-date-${task_count} task-${task.id}" style="display:none;padding-left: 10px;font-size: 9px;font-weight: normal;color:#4A4A4A">${showDate}</div>
                        </div>`);
          }
        }
        temp.appendTo(self.render_task_list);
        const task_div = document.querySelector(`#task_name_${task_count}`);
        if (task_div) {
          task_div.onclick = e => self.click_task_name(e);
        }
        if (weeks) {
          const task_div_week = document.querySelector(`#task_week_${task_count}`);
          if (task_div_week) {
            task_div_week.onclick = e => self.click_task_week_name(e);
          }
        }
        if (this.config.hover_date_show == true) { // MouseOver on list
          if (task_div) {
            task_div.onmouseover = e => self.hover_task_name(e, task);
          }
          const _this = this;
          $('.grid-row').on('mouseenter', function () {
            const index = $(this).index();
            _this.unselect_taskname_all();
            $(this).attr('active', 1);
            $(`.task-${index}`).attr('active', 1);
            $('.task-list-item-date').hide();
            $(`.task-date-${index}`).show();
          });
        }
      }
      task_count++;
    }
  }

  hover_task_name(e, task) {
    const { id } = e.target;
    const index = id.replace('task_name_', '');
    const parent = $(e.target).parent();
    const active = $(parent).attr('active') == 1 ? 0 : 1;
    this.unselect_taskname_all();
    $(parent).attr('active', active);
    $('rect.grid-row').eq(index).attr('active', active);
    $('.task-list-item-date').hide();
    $(`.task-date-${index}`).show();
  }

  click_task_name(e) {
    console.log(`click_task_name${e.target.id}`);
    const { id } = e.target;
    const index = id.replace('task_name_', '');
    const parent = $(e.target).parent();
    const active = $(parent).attr('active') == 1 ? 0 : 1;
    this.unselect_taskname_all();
    $(parent).attr('active', active);
    $('rect.grid-row').eq(index).attr('active', active);
    this.trigger_event('task_list_name', [this.tasks[index], index, active]);
  }

  click_task_week_name(e) {
    console.log(`click_task_list_week_name${e.target.id}`);
    const { id } = e.target;
    const index = id.replace('task_week_', '');
    const parent = $(e.target).parent();
    const active = 1;
    this.unselect_taskname_all();
    $(parent).attr('active', active);
    $('rect.grid-row').eq(index).attr('active', active);
    this.trigger_event('task_list_week_name', [id]);
  }

  get_column_width() {
    let colwidth = this.config.column_width;
    if (this.view_is('Week')) {
      colwidth /= 7;
    } else if (this.view_is('Month')) {
      colwidth /= 303;
    }
    return colwidth;
  }

  make_grid_header() {
    const self = this;
    const colwidth = this.get_column_width();
    let header_width = self.dates.length * colwidth;
    const header_height = self.config.header_height + 10;
    self.header_height = header_height;
    if (self.config.show_task_list) {
      header_width += self.config.task_list_width;
    }
    let _h = header_height / 2;
    let high = _h;
    const viewmode = this.get_view_mode();
    self.canvas.rect(0, 0, header_width, header_height)
      .addClass(`grid-header ${viewmode}-grid-header `)
      .appendTo(self.element_groups.grid);
    self.canvas.rect(0, 0, header_width, _h)
      .addClass('date-title-header')
      .appendTo(self.element_groups.grid);
    if (self.view_is('Week')) {
      high = _h / 2;
      self.canvas.rect(0, _h, header_width, high)
        .addClass('date-value-header1')
        .appendTo(self.element_groups.grid);
      _h += high;
    }
    self.canvas.rect(0, _h, header_width, high - 1)
      .addClass('date-value-header')
      .appendTo(self.element_groups.grid);
    if (self.config.show_task_list) {
      const show_task_list_width = self.config.task_list_width;
      if (self.render_task_list) {
        self.render_task_list.innerHTML = '';
        const header_height = self.config.header_height + 10;
        const _h = header_height / 2;
        const h_element = document.querySelector('.task_list_header');
        if (h_element != null) {
          h_element.remove();
        }
        const scales = this.getScales();
        const viewscale = scales.find(s => s.id == this.config.view_mode);
        let sortDiv = '';
        const enabledSorting = (this.config.sorting.enabled === true);
        if (enabledSorting) {
          const sorting_values = this.getSortingValues();
          const sortingItems = sorting_values.find(s => s.id == this.config.sorting.id);
          sortDiv = `<div class="task_list_header_date" style="height:${_h}px;">
                        <div class="gantt-sort-wrapper">
                            <span style="padding-right: 35px;">Sort:</span>
                            <span class="clickable" id="gantt-sorting" data-value="${sortingItems.id}">
                                <span>${sortingItems.title}</span>
                                <span class="timescale-icon"></span>
                            </span>
                        </div>
                    </div>`;
        }

        const temp = $(`<div class="task_list_header ${viewmode}-task-list-header" style="height:${header_height - 1}px;">
                        <div class="task_list_header_month" style="height:${_h}px;">
                            <div class="gantt-timescale-wrapper">
                                <span>Timescale:</span>
                                <span class="clickable" id="gantt-timescale" data-value="${viewmode}">
                                    <span>${viewscale.title}</span>
                                    <span class="timescale-icon"></span>
                                </span>
                            </div>
                        </div>
                        ${sortDiv}
                </div>
                `);
        temp.appendTo(self.render_task_list);
        const timescale_div = document.querySelector('#gantt-timescale');
        timescale_div.onclick = e => self.show_dropdown_popup(e, 'scale');
        if (enabledSorting) {
          const timeline_sort_div = document.querySelector('#gantt-sorting');
          timeline_sort_div.onclick = e => self.show_dropdown_popup(e, 'sort');
        }
      }
    }
  }

  getScales() {
    return [{ id: 'Day', title: 'Days' }, { id: 'Week', title: 'Weeks' }, { id: 'Month', title: 'Months' }];
  }

  getSortingValues() {
    return this.config.sorting.items || [];
  }

  hideDropdownPopup() {
    const dropdown = document.querySelector('.gantt-show-dropdown-popup');
    if (dropdown != null) {
      dropdown.remove();
    }
  }

  show_dropdown_popup(e, type) {
    console.log(`type-->${type}`);
    const { header_height } = this.config;
    this.hideDropdownPopup();
    if (type == 'sort') {
      const sorting_values = this.getSortingValues();
      let sortingText = '';
      sorting_values.map(sort => sortingText += `<div class="gantt-dp-item clickable">${sort.title}</div>`);
      const tempSorting = $(`<div class="gantt-show-dropdown-popup shadow" style="margin-top:0px; margin-left: 65px;">
                 ${sortingText}
             </div>`);
      tempSorting.appendTo(document.querySelector('.task_list_header'));
      const sortingItems = document.querySelectorAll('.gantt-show-dropdown-popup .gantt-dp-item');
      if (sortingItems.length) {
        sortingItems.forEach((e, index) => {
          e.onclick = (e) => {
            this.hideDropdownPopup();
            this.change_sorting_view(sorting_values[index].id);
          };
        });
      }
    } else if (type == 'scale') {
      const scales = this.getScales();
      let scalesText = '';
      const enabledSorting = (this.config.sorting.enabled === true);
      scales.map(scale => scalesText += `<div class="gantt-dp-item clickable">${scale.title}</div>`);
      const marTop = enabledSorting ? -30 : 0;
      const tempScales = $(`<div class="gantt-show-dropdown-popup shadow" style="margin-top:${marTop}px;margin-left: 60px;">
                ${scalesText}
            </div>`);
      tempScales.appendTo(document.querySelector('.task_list_header'));
      const scaleItems = document.querySelectorAll('.gantt-show-dropdown-popup .gantt-dp-item');
      if (scaleItems.length) {
        scaleItems.forEach((e, index) => {
          e.onclick = (e) => {
            this.hideDropdownPopup();
            this.change_view_mode(scales[index].id);
          };
        });
      }
    }
  }

  make_grid_ticks() {
    const self = this;
    let tick_x = 0;
    const tick_y = (self.config.line_tick_y) ? self.config.line_tick_y : self.header_height;
    const tick_height = (self.config.line_tick_y) ? (self.config.bar.height + self.config.padding) * self.tasks.length + 23.5 : (self.config.bar.height + self.config.padding) * self.tasks.length;
    if (self.config.show_task_list) {
      const show_task_list_width = self.config.task_list_width;
      self.canvas.path(Snap.format('M {x} {y} v {height}', {
        x: show_task_list_width,
        y: tick_y,
        height: tick_height,
      }))
        .addClass('leftside-panel')
        .appendTo(self.element_groups.grid);
      tick_x = show_task_list_width;
    }
    self.gantt_start_x_pos = tick_x;
    const viewmode = this.get_view_mode();
    const viewDayMode = (viewmode == 'day');
    const viewWeekMode = (viewmode == 'week');
    const viewMonthMode = (viewmode == 'month');
    const colwidth = this.get_column_width();
    let i = 0;
    for (const date of self.dates) {
      let tick_class = `tick ${viewmode}-tick`;
      const tike_y1 = tick_height + (self.config.bar.height + self.config.padding);
      // thick tick for monday
      if (viewDayMode && date.date() === 1) {
        tick_class += ' thick day-thick';
      }
      // thick tick for first week
      if (viewWeekMode && date.weekday() == 1) {
        tick_class += ' thick week-thick';
      }
      // thick ticks for quarters
      if (viewMonthMode && date.date() == 1) {
        tick_class += ' month-start-tick ';
        if (date.month() % 3 === 0) {
          tick_class += ' thick month-thick';
        }
      }
      self.gantt_end_x_pos = tick_x;
      self.canvas.path(Snap.format('M {x} {y}  v {height}', {
        x: tick_x,
        y: tick_y,
        height: tick_height,
      }))
        .addClass(tick_class)
        .appendTo(self.element_groups.grid);
      tick_x += colwidth;
      self.make_grid_highlights(date, i, tick_x, colwidth, viewmode);
      i++;
    }
  }

  get_view_mode() {
    return this.config.view_mode.toLowerCase();
  }

  get_utc_startofday() {
    const startOfDay = moment.utc().startOf('day').format('X');
    const v = parseInt(startOfDay) * 1000 - this.get_utc_offset_value();
    return v;
  }

  make_grid_highlights(date, i = 0, tick_x = 0, colwidth, viewmode) {
    // highlight today's date
    const self = this;
    const h = self.header_height;
    const startOfDay = this.get_utc_startofday();
    let x = moment(startOfDay).diff(self.gantt_start, 'hours')
        / self.config.step * self.config.column_width;
    if (self.config.show_task_list) {
      x += self.config.task_list_width;
    }
    const y = 0;
    const width = colwidth;
    const height = (self.config.bar.height + self.config.padding) * self.tasks.length;
    const dayMode = (viewmode == 'day');
    const weekMode = (viewmode == 'week');
    const monthMode = (viewmode == 'month');
    let header_thick_class = '';
    const weekendDays = date.format('dd').toLowerCase();
    if (dayMode && date.daysInMonth() === date.date()) {
      header_thick_class = ' header-thick-day';
    } else if (weekMode && weekendDays == 'su') {
      header_thick_class = ' header-thick-week';
    } else if (monthMode && date.month() % 3 === 0 && date.date() === 1) {
      header_thick_class = ' header-thick-month';
    }
    if (dayMode || (weekMode && weekendDays == 'su') || (monthMode && date.date() === 1)) {
      tick_x = monthMode ? tick_x - colwidth : tick_x;
      self.canvas.path(Snap.format('M {x} {y}  v {height}', {
        x: tick_x,
        y: h / 2,
        height: h / 2,
      }))
        .addClass(`header-${viewmode} ${header_thick_class}`)
        .appendTo(self.element_groups.date);
    }
    if (date.date() === date.daysInMonth() && (dayMode || weekMode || monthMode && date.month() === 11)) {
      self.canvas.path(Snap.format('M {x} {y}  v {height}', {
        x: tick_x,
        y: 0,
        height: h / 2,
      }))
        .addClass(`upper-rect header-upper-rect ${viewmode}-header-upper-rect`)
        .appendTo(self.element_groups.date_headers);
    }
    const renderPage = self.config.renderpage;
    let add_x = 0;
    if (renderPage == 'workload') {
      add_x = 5;
    }
    if (date.isSame(moment(startOfDay), 'd')) {
      if (dayMode || weekMode) {
        self.canvas.rect(x + add_x, self.header_height, width, height)
          .addClass('today-highlight')
          .appendTo(self.element_groups.grid);
      }
      if (dayMode || (weekMode && renderPage == 'workload')) {
        self.make_header_highlight(x + add_x, h / 2, width, h / 2);
      } else if (weekMode) {
        const day = moment(startOfDay).format('d') - 1;
        const week_x = x - day * width;
        self.make_header_highlight(week_x + add_x, h - h / 4 + 3, width * 7, h / 4 - 3);
      } else if (monthMode) {
        const day = moment(startOfDay).date();
        let week_x = x - day * width;
        week_x = week_x < 0 ? 0 : week_x + colwidth;
        self.make_header_highlight(week_x, h / 2, width * date.daysInMonth(), h / 2);
      }
    }
    const h2 = h / 2 - 1;
    x = 0;
    if (monthMode) {
      x = date.diff(self.gantt_start, 'hours') / 24 * colwidth;
    } else {
      x = date.diff(self.gantt_start, 'hours') / self.config.step * self.config.column_width;
    }
    if (self.config.show_task_list) {
      x += self.config.task_list_width;
    }
    if (dayMode || weekMode) {
      if (weekendDays == 'sa' || weekendDays == 'su') {
        self.canvas.rect(x, self.header_height, width, height)
          .addClass('weekdays-highlight')
          .appendTo(self.element_groups.grid);
      }
    }
  }

  make_header_highlight(x, y, w, h) {
    this.canvas.rect(x, y, w, h)
      .addClass('header-highlight')
      .appendTo(this.element_groups.grid);
  }

  make_dates() {
    const self = this;
    const viewWeekMode = this.view_is('Week');
    const viewmode = this.get_view_mode();
    // console.log(self.dates);
    for (const date of self.get_dates_to_draw()) {
      const todayClass = date.is_today ? ' gantt-today-date ' : '';
      const weekDaysClass = date.is_weekend && viewWeekMode ? ' gantt-weekdays-date ' : '';
      const renderPage = self.config.renderpage;
      let add_y = 5; let
        add_x = 0;
      if (renderPage == 'workload') {
        add_y = 3;
        add_x = 5;
      }
      if (viewWeekMode) {
        if (date.start_ofweek) {
          self.canvas.text(date.lower_x - add_x, date.lower_y - add_y, date.week_day)
            .addClass('gantt-week-text ')
            .appendTo(self.element_groups.date);
        }
        date.lower_y += 7;
      }
      self.canvas.text(date.lower_x, date.lower_y, date.lower_text)
        .addClass(`lower-text ${viewmode}-lower-text ${todayClass + weekDaysClass}`)
        .appendTo(self.element_groups.date);
      if (date.upper_text) {
        const $upper_text = self.canvas.text(date.upper_x, date.upper_y, date.upper_text)
          .addClass('upper-text')
          .appendTo(self.element_groups.date_headers);
        // remove out-of-bound dates
        if ($upper_text.getBBox().x2 > self.element_groups.grid.getBBox().width) {
          $upper_text.remove();
        }
      }
    }
  }

  get_dates_to_draw() {
    let last_date = null; const
      self = this;
    const dates = self.dates.map((date, i) => {
      const d = self.get_date_info(date, last_date, i);
      last_date = date;
      return d;
    });
    return dates;
  }

  get_date_info(date, last_date, i) {
    const self = this;
    const viewmode = self.config.view_mode;
    if (!last_date) {
      last_date = date.clone().add(1, 'year');
    }
    const header_format = self.config.header_month_format;
    const colwidth = this.get_column_width();
    const weekText = date.format('dd').slice(0, 1);
    const diffMonth = date.month() !== last_date.month();
    const date_text = {
      Day_lower: date.format('D'),
      Week_lower: (weekText == 'S') ? '' : weekText,
      Month_lower: (i == 0 || diffMonth) ? date.format('MMMM') : '',
      Day_upper: diffMonth ? date.format('MMMM YYYY') : (i == 0 && date.date() < 20 ? date.format('MMMM YYYY') : ''),
      Week_upper: diffMonth ? date.format('MMM YYYY') : (i == 0 && date.date() < 26 ? date.format('MMM YYYY') : ''),
      Month_upper: date.year() !== last_date.year() ? date.format('YYYY') : '',
    };

    let x_width = 0;
    if (self.config.show_task_list) {
      x_width = self.config.task_list_width;
    }
    x_width += i * colwidth;
    const base_pos = {
      lower_x: x_width,
      upper_x: x_width,
      lower_y: self.config.header_height,
      upper_y: self.config.header_height - 25,
    };
    const x_pos = {
      Day_lower: self.config.column_width / 2,
      Day_upper: 10,
      Week_lower: colwidth / 2,
      Week_upper: 10,
      Month_lower: self.config.column_width / 12 / 2,
      Month_upper: 10,
    };
    const weekendDays = date.format('dd').toLowerCase();
    return {
      upper_text: date_text[`${viewmode}_upper`],
      lower_text: date_text[`${viewmode}_lower`],
      upper_x: base_pos.upper_x + x_pos[`${viewmode}_upper`],
      upper_y: base_pos.upper_y,
      lower_x: base_pos.lower_x + x_pos[`${viewmode}_lower`],
      lower_y: base_pos.lower_y,
      is_today: date.isSame(moment(), 'd'),
      is_weekend: (weekendDays == 'su' || weekendDays == 'sa'),
      week_day: date.format('D'),
      start_ofweek: (weekendDays == 'mo'),
    };
  }

  set_width() {
    const self = this;
    const cur_width = self.canvas.node.getBoundingClientRect().width;
    const actual_width = self.canvas.select('#grid .grid-row').attr('width');
    if (cur_width < actual_width) {
      self.canvas.attr('width', actual_width);
    }
  }

  set_scroll_position() {
    try {
      const self = this;
      const parent_element = document.querySelector(self.element).parentElement;
      if (!parent_element) return;
      let scroll_pos = self.get_min_date() ? self.get_min_date().diff(self.gantt_start, 'hours')
                / self.config.step * self.config.column_width - self.config.column_width : 0;
      if (self.config.focus_today) {
        scroll_pos = moment().diff(self.gantt_start, 'hours')
                / self.config.step * self.config.column_width - self.config.column_width;
      }
      parent_element.scrollLeft = scroll_pos;
    } catch (err) {
      console.log(err);
    }
  }

  get_min_date() {
    const self = this;
    const task = self.tasks.reduce((acc, curr) => ((curr._start && curr._start.isSameOrBefore(acc._start)) ? curr : acc));
    return task._start;
  }

  make_bars() {
    const self = this;
    if (self.config.shadecolor) {
      self.overlapping_tasks();
    }

    self.tasks.map((task) => {
      if (task.data) {
        self._bars = task.data.map((item) => {
          const bar = Bar(self, item);
          self.element_groups.bar.add(bar.group);
          return bar;
        });
      }
    });
  }

  group_by_key(data, key) {
    return data.reduce((item, k) => {
      (item[k[key]] = item[k[key]] || []).push(k);
      return item;
    }, {});
  }

  /*
    * @see hexdec http://locutus.io/php/math/dechex/index.html
    */
  hexdec(hexString) {
    hexString = (`${hexString}`).replace(/[^a-f0-9]/gi, '');
    return parseInt(hexString, 16);
  }

  /*
    * @see http://locutus.io/php/math/hexdec/
    */
  dechex(number) {
    if (number < 0) {
      number = 0xFFFFFFFF + number + 1;
    }
    return parseInt(number, 10).toString(16);
  }

  shadeColor2(color, percent) {
    color = color.replace('#', '');
    const t = percent < 0 ? 0 : 255;
    const p = percent < 0 ? percent * -1 : percent;
    const R = this.hexdec(color.substr(0, 2));
    const G = this.hexdec(color.substr(2, 2));
    const B = this.hexdec(color.substr(4, 2));
    const code = this.dechex(0x1000000 + (Math.round((t - R) * p) + R) * 0x10000 + (Math.round((t - G) * p) + G) * 0x100 + (Math.round((t - B) * p) + B));
    return `#${code.substr(1)}`;
  }

  overlapping_tasks() {
    const self = this;
    const { tasks } = self;
    tasks.map((item) => {
      if (item.data) {
        // let items = item.data.sort((a, b) => b.days - a.days);
        // convert data into group object by days key.
        const groupItems = self.group_by_key(item.data, 'days');
        let items = [];
        if (Object.keys(groupItems).length) {
          for (const i in groupItems) {
            // sorting data
            groupItems[i] = groupItems[i].sort((a, b) => a._start > b._start);
            // concat to array
            const flattern = groupItems[i].reduce((a, b) => a.concat(b), []);
            if (i < 0) {
              // push data to array first.
              items = items.reduce((a, b) => a.concat(b), groupItems[i]);
            } else {
              // concat data to array.
              items = groupItems[i].reduce((a, b) => a.concat(b), items);
            }
          }
        }
        // array reverse based on days.
        items = items.reverse();
        if (items.length) {
          items = items.map((item, index) => {
            let count = 0;
            item.is_overlapping = false;
            item.overlapping_items = [];
            if (index != 0) {
              // call recursively, if start, end dates are there in previous items.
              for (let x = 0; x < index; x++) {
                const s = (item._start >= items[x]._start && item._start <= items[x]._end);
                const e = (item._end >= items[x]._start && item._end <= items[x]._end);
                if (s || e) {
                  item.is_overlapping = true;
                  item.overlapping_items.push(items[x]);
                  count++;
                }
              }
            }
            // calculate the shadecolor
            const overlapping_colors_length = self.config.overlapping_colors.length;
            const _color = (count >= overlapping_colors_length) ? overlapping_colors_length - 1 : count;
            item.color = self.config.overlapping_colors[_color];
            if (item.is_overlapping) {
              item.overlapping_items.unshift(item);
            }
            return item;
          });
        }
        item.data = items;
      }
    });
  }

  bind_grid_click() {
    const self = this;
    if (self.config.show_task_list) {
      self.element_groups.grid.selectAll('.task-list-name').forEach((ele, index, arr) => {
        ele.removeClass('active');
        ele.attr('id', `task-list-id-${index}`);
        ele.click((e) => {
          let active = $(e.target).attr('active');
          self.unselect_taskname_all();
          active = (active == 1 ? 0 : 1);
          $(e.target).attr('active', active);
          $('rect.grid-row').eq(index).attr('active', active);
          self.trigger_event('task_list_name', [self.tasks[index], index, active]);
        });
      });
    }

    self.element_groups.grid.click(() => {
      self.unselect_all();
      self.element_groups.details
        .selectAll('.details-wrapper')
        .forEach(el => el.addClass('hide'));
    });
    // var date = new Date();
    // var seconds = date.getTime();
    // console.log("bind_grid_click time-->"+seconds);
  }

  unselect_all() {
    this.canvas.selectAll('.bar-wrapper').forEach((el) => {
      el.removeClass('active');
    });
    this.canvas.selectAll('.bar-wrapper .bar-details foreignObject').forEach((e2) => {
      e2.remove();
    });
  }

  unselect_taskname_all() {
    this.element_groups.grid.selectAll('rect.grid-row').forEach((el) => {
      el.removeClass('active');
      el.attr('active', 0);
    });
    if (typeof NodeList.prototype.forEach === 'undefined') {
      NodeList.prototype.forEach = Array.prototype.forEach;
    }
    const taskListItems = document.querySelectorAll('.task-list-item');
    if (taskListItems.length) {
      taskListItems.forEach((e2) => {
        e2.setAttribute('active', 0);
      });
    }
  }

  view_is(modes) {
    const view_modes = this.view_modes();
    if (typeof modes === 'string') {
      return this.config.view_mode === modes;
    } if (Array.isArray(modes)) {
      for (const mode of view_modes) {
        if (mode === modes) return true;
      }
    }
  }

  view_modes() {
    return ['Day', 'Week', 'Month'];
  }

  trigger_event(event, args) {
    if (this._events[`on_${event}`]) {
      this._events[`on_${event}`].apply(null, args);
    }
  }
}

/**
 * @react component
 * Gantt chart
 */
class GanttChartComp extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      tasks: this.props.tasks || [],
      ganttInstance: null,
      loading: this.props.loading || false,
    };
    this.showLoader = this.showLoader.bind(this);
    this.hideLoader = this.hideLoader.bind(this);
  }

  componentDidMount() {
    const ganttChartOptions = {};
    ganttChartOptions.config = this.props.config;
    ganttChartOptions.tasks = this.state.tasks;
    ganttChartOptions.events = this.props.events;
    if (this.state.loading && this.state.tasks.length) {
      this.showLoader();
    }
    this.state.ganttInstance = new GanttChart(`#${this.refs.ganttSvg.id}`, ganttChartOptions);
  }

  showLoader() {
    showLoaderWidget($('#content'));
  }

  hideLoader() {
    if (this.state.loading) {
      setTimeout(() => removeLoaderWidget(), 500);
    }
  }

  render() {
    const _class = this.props.class || '';
    this.hideLoader();
    return (
      <div className={`gantt-chart-wrapper ${_class}`}>
        <svg id="gantt" width="400" height="250" ref="ganttSvg" />
        <div className="memberAiPopupArrow" />
        <div id="gantt-popup" className="shadow" />
      </div>
    );
  }
}
GanttChartComp.defaultProps = {
  config: {},
  events: {},
};

export default GanttChartComp;
